import { useRef, MouseEvent, ReactNode } from "react";

interface MagneticButtonProps {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  strength?: number; // px max displacement
}

/**
 * A wrapper that gently pulls its child element toward the cursor.
 * Wrap your existing button/link as a child and it'll get the effect.
 */
export default function MagneticButton({
  children,
  className = "",
  innerClassName = "",
  strength = 14,
}: MagneticButtonProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;
    const rect = wrap.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    const dx = Math.max(-strength, Math.min(strength, (relX / (rect.width / 2)) * strength));
    const dy = Math.max(-strength, Math.min(strength, (relY / (rect.height / 2)) * strength));
    inner.style.transform = `translate(${dx}px, ${dy}px)`;
  };

  const reset = () => {
    if (innerRef.current) innerRef.current.style.transform = "translate(0px, 0px)";
  };

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      className={`inline-block ${className}`}
    >
      <div
        ref={innerRef}
        className={`inline-block transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${innerClassName}`}
      >
        {children}
      </div>
    </div>
  );
}